import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "../../utils/utils";
import ThumbIcon from "../../assets/svgs/common/thumb.svg";
import millify from "millify";

const Slider = React.forwardRef<
	React.ElementRef<typeof SliderPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
		step?: number;
		max?: number;
		stepsVisible?: boolean;
		overLabelVisible?: boolean;
		thumbWidth?: string;
		thumbHeight?: string;
		trackHeight?: string;
	}
>(
	(
		{
			className,
			max = 20,
			step,
			stepsVisible,
			overLabelVisible,
			thumbHeight,
			thumbWidth,
			trackHeight,
			...props
		},
		ref,
	) => {
		const value = props.value ?? [0];

		const numDots = 20; // Number of dots to render for performance
		const dotStep = max / numDots;

		return (
			<SliderPrimitive.Root
				ref={ref}
				className={cn(
					"relative flex w-full touch-none select-none items-center",
					className,
				)}
				{...props}
				value={value}
				max={max}
				step={step}
				onValueChange={props.onValueChange}
			>
				<SliderPrimitive.Track
					className={`relative flex justify-center items-center bg-slidertrack rounded-full w-full cursor-pointer overflow-hidden grow ${trackHeight ? trackHeight : "h-5"}`}
				>
					<SliderPrimitive.Range className="absolute bg-sliderrange h-full" />
					<div className="relative flex justify-evenly items-center w-full">
						{stepsVisible &&
							[...Array(numDots + 1)].map((_, index) => {
								const dot = index * dotStep;
								return (
									<div
										// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
										key={index}
										className={` h-1 w-1 cursor-pointer  rounded-full ${
											dot <= (value.at(0) ?? 0)
												? "bg-black"
												: "bg-white bg-opacity-20"
										}`}
										style={{ left: `${(dot / max) * 100}%` }}
									/>
								);
							})}
					</div>
				</SliderPrimitive.Track>
				<SliderPrimitive.Thumb className="relative focus-visible:outline-none flex flex-col items-center disabled:opacity-50 cursor-pointer disabled:pointer-events-none">
					<ThumbIcon width={thumbWidth} height={thumbHeight} />
					{overLabelVisible && (
						<div className="-top-12 absolute backdrop-blur">
							<div className="relative flex flex-col items-center px-2 py-1 border rounded font-semibold">
								<span className="flex">
									₹{Number.isNaN(value[0]) ? 0 : millify(value[0])}
								</span>
								<div className="-bottom-1.5 z-10 absolute bg-background border-r border-b w-3 h-3 rotate-45" />
							</div>
						</div>
					)}
				</SliderPrimitive.Thumb>
			</SliderPrimitive.Root>
		);
	},
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
